import React, { useState, useEffect } from 'react';
import { Card } from '../components/Card';
import { Loading } from '../components/Loading';
import { Button } from '../components/Button';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/userService';
import { backupService } from '../services/backupService';
import { User } from '../types';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import {
  IconUsers,
  IconCheckCircle,
} from '../components/icons';

export const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const usersResponse = await userService.getUsers();

      if (usersResponse.success && usersResponse.data) {
        setUsers(usersResponse.data);
      }
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const [backups, setBackups] = useState<any[]>([]);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupError, setBackupError] = useState('');
  const [backupSuccess, setBackupSuccess] = useState('');

  useEffect(() => {
    if (user?.role === 'admin') {
      loadBackups();
    }
  }, [user]);

  const loadBackups = async () => {
    try {
      const response = await backupService.listBackups();
      if (response.success && response.data) {
        setBackups(response.data);
      }
    } catch (err) {
      console.error('Failed to load backups:', err);
    }
  };

  const handleCreateBackup = async () => {
    try {
      setBackupLoading(true);
      setBackupError('');
      setBackupSuccess('');
      const response = await backupService.createBackup();
      if (response.success) {
        setBackupSuccess('Backup created successfully');
        loadBackups();
      } else {
        setBackupError(response.message || 'Failed to create backup');
      }
    } catch (err: any) {
      setBackupError(err.message || 'Failed to create backup');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleDownloadBackup = async (filename: string) => {
    try {
      const blob = await backupService.downloadBackup(filename);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setBackupError(err.message || 'Failed to download backup');
    }
  };

  const totalUsers = users.length;
  const clientUsers = users.filter((u) => u.role === 'client').length;

  if (loading) {
    return <Loading fullScreen text="Loading dashboard..." />;
  }

  return (
    <div className="p-3 sm:p-4 md:p-6">
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">System Management Dashboard for noor</h1>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6 mb-4 sm:mb-6">
        <Card className="p-3 sm:p-4">
          <div className="text-center">
            <p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2">Total Users</p>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900">{totalUsers}</p>
            <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">Manage system users</p>
          </div>
        </Card>
        <Card className="p-3 sm:p-4">
          <div className="text-center">
            <p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2">Active Clients</p>
            <p className="text-2xl sm:text-3xl font-bold text-blue-600">{clientUsers}</p>
            <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">Client accounts</p>
          </div>
        </Card>
        <Card className="p-3 sm:p-4">
          <div className="text-center">
            <p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2">System Status</p>
            <div className="flex justify-center mb-1 sm:mb-2">
              <IconCheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
            </div>
            <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">All systems operational</p>
          </div>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6 mb-4 sm:mb-6">
        <Card title="Quick Actions" className="p-3 sm:p-4 md:p-5">
          <div className="space-y-2 sm:space-y-3">
            <Link
              to="/users"
              className="block p-3 sm:p-4 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm sm:text-base font-semibold text-blue-900">Manage Users</p>
                  <p className="text-xs sm:text-sm text-blue-700 mt-0.5 sm:mt-1">Register and manage client accounts</p>
                </div>
                <IconUsers className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 flex-shrink-0" />
              </div>
            </Link>
          </div>
        </Card>

        {/* Backup & Restore Section */}
        <Card title="Backup & Restore" className="p-3 sm:p-4 md:p-5">
          {backupError && (
            <div className="mb-3 sm:mb-4 p-2 sm:p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-xs sm:text-sm">
              {backupError}
            </div>
          )}
          {backupSuccess && (
            <div className="mb-3 sm:mb-4 p-2 sm:p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-xs sm:text-sm">
              {backupSuccess}
            </div>
          )}
          <div className="space-y-3 sm:space-y-4">
            <Button onClick={handleCreateBackup} disabled={backupLoading} className="w-full sm:w-auto">
              {backupLoading ? 'Creating Backup...' : 'Create Backup'}
            </Button>
            {backups.length > 0 && (
              <div>
                <h3 className="text-xs sm:text-sm font-semibold text-gray-700 mb-2">Available Backups</h3>
                <div className="space-y-2">
                  {backups.slice(0, 5).map((backup) => (
                    <div
                      key={backup.filename}
                      className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3 p-2 sm:p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">{backup.filename}</p>
                        <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">
                          {(backup.size / 1024).toFixed(2)} KB • {format(new Date(backup.created_at), 'MMM dd, yyyy HH:mm')}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleDownloadBackup(backup.filename)}
                        className="w-full sm:w-auto"
                      >
                        Download
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};
